import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiBarChart2 as BarChart3, FiTrendingUp as TrendingUp, FiCheckCircle as CheckCircle,
  FiXCircle as XCircle, FiClock as Clock, FiRefreshCw as RefreshCw,
  FiDollarSign as DollarSign, FiAlertCircle as AlertCircle,
  FiArrowRight as ArrowRight,
} from 'react-icons/fi';
import { getEvaluationMetrics } from '../../../../../services/aiOperationsApi';
import DateRangeFilter from '../../../../../components/dashboard/DateRangeFilter';
import { dateRangeToParams } from '../../../../../components/dashboard/widgetDataFetcher';
import type { DateRange } from '../../../../../components/dashboard/dateRangeUtils';
import type { EvaluationRun } from './RunDisplay';

const OverviewContent = () => {
  const [metrics, setMetrics] = useState<{ summary?: { total_runs?: number; total_evaluations?: number; avg_score?: number; avg_duration?: number; pass_rate?: number; total_cost?: number; [key: string]: unknown }; by_model?: Array<{ model_id?: string; model?: string; run_count?: number; count?: number; avg_score?: number; avg_latency?: number }>; recent_evaluations?: EvaluationRun[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({ preset: 'last_7_days', startDate: null, endDate: null });

  useEffect(() => {
    loadMetrics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const loadMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = dateRangeToParams(dateRange);
      const data = await getEvaluationMetrics(params.days, params.start_date, params.end_date);
      setMetrics(data);
    } catch (err: unknown) {
      console.error('Error loading evaluation metrics:', err);
      setError((err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number | null | undefined) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  const formatPercentage = (num: number | null | undefined) => {
    if (!num && num !== 0) return '0%';
    return `${num.toFixed(1)}%`;  // Backend already sends percentage (0-100)
  };

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms && ms !== 0) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatCost = (cost: number | null | undefined) => {
    if (!cost && cost !== 0) return '$0.00';
    return `$${cost.toFixed(4)}`;
  };

  const getScoreColor = (score: number | null | undefined) => {
    if (!score && score !== 0) return 'text-gray-600';
    const normalizedScore = score > 1 ? score : score * 100;
    if (normalizedScore >= 80) return 'text-green-600';
    if (normalizedScore >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusBadge = (status: string | null | undefined) => {
    const statusConfig = {
      completed: {
        bg: 'bg-green-100 dark:bg-green-900/50',
        text: 'text-green-800 dark:text-green-300',
        icon: CheckCircle,
        tooltip: 'Evaluation finished successfully with all test cases processed'
      },
      running: {
        bg: 'bg-blue-100 dark:bg-blue-900/50',
        text: 'text-blue-800 dark:text-blue-300',
        icon: RefreshCw,
        tooltip: 'Evaluation is currently running - results will appear when complete'
      },
      pending: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/50',
        text: 'text-yellow-800 dark:text-yellow-300',
        icon: Clock,
        tooltip: 'Evaluation is queued and waiting to start'
      },
      partial: {
        bg: 'bg-orange-100 dark:bg-orange-900/50',
        text: 'text-orange-800 dark:text-orange-300',
        icon: AlertCircle,
        tooltip: 'Evaluation completed with some test cases skipped or errored'
      },
      failed: {
        bg: 'bg-red-100 dark:bg-red-900/50',
        text: 'text-red-800 dark:text-red-300',
        icon: XCircle,
        tooltip: 'Evaluation failed due to an error - check logs for details'
      },
      throttled: {
        bg: 'bg-amber-100 dark:bg-amber-900/50',
        text: 'text-amber-800 dark:text-amber-300',
        icon: RefreshCw,
        tooltip: 'Evaluation paused due to API rate limits - will resume automatically'
      },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
        title={config.tooltip}
      >
        <Icon className={`w-3.5 h-3.5 ${status === 'running' || status === 'throttled' ? 'animate-spin' : ''}`} />
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-300">Error loading evaluation metrics: {error}</p>
      </div>
    );
  }

  const evaluations = metrics?.recent_evaluations || [];
  const byModel = metrics?.by_model || [];
  const summary = metrics?.summary || {};

  const maxScore = byModel.length > 0
    ? Math.max(...byModel.map((m) => m.avg_score || 0))
    : 100;

  return (
    <div>
      {/* Time Range Filter */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Evaluation Overview</h2>
        <div className="flex items-center gap-3">
          <DateRangeFilter
            value={dateRange}
            onChange={(v: DateRange) => setDateRange(v)}
            compact={true}
          />
          <button
            onClick={loadMetrics}
            disabled={loading}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Runs</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(summary.total_evaluations || 0)}
              </p>
            </div>
            <BarChart3 className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Score</p>
              <p className={`text-2xl font-bold mt-1 ${getScoreColor(summary.avg_score)}`}>
                {summary.avg_score ? formatPercentage(summary.avg_score) : '0%'}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Pass Rate</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                {formatPercentage(summary.pass_rate || 0)}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Duration</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatDuration(summary.avg_duration || 0)}
              </p>
            </div>
            <Clock className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Cost</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCost(summary.total_cost || 0)}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
      </div>

      {/* Performance by Model Chart */}
      {byModel.length > 0 && (
        <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Performance by Model</h2>
          <div className="space-y-4">
            {byModel.map((model) => {
              const avgScore = model.avg_score || 0;
              let modelScoreBarColor: string;
              if (avgScore >= 0.8) modelScoreBarColor = 'bg-green-600';
              else if (avgScore >= 0.6) modelScoreBarColor = 'bg-yellow-600';
              else modelScoreBarColor = 'bg-red-600';
              return (
              <div key={model.model || 'unknown'} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">{model.model || 'Unknown Model'}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 dark:text-gray-400">{formatNumber(model.count || 0)} runs</span>
                    <span className={`font-semibold ${getScoreColor(model.avg_score)}`}>
                      {model.avg_score ? formatPercentage(model.avg_score) : '0%'}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full ${modelScoreBarColor}`}
                    style={{ width: `${(avgScore / (maxScore > 1 ? maxScore : 1)) * 100}%` }}
                  ></div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Evaluations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm transition-colors overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Recent Evaluations ({evaluations.length})
          </h2>
        </div>

        {evaluations.length === 0 ? (
          <div className="p-12">
            <div className="max-w-md mx-auto text-center">
              <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-7 h-7 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No Evaluation Results Yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Run evaluations against your datasets to see performance metrics, scores, and trends here.
              </p>
              <Link
                to="/ai-operations/registry?tab=prompts"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Go to Prompts
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Timestamp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dataset</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {evaluations.map((evaluation) => (
                  <tr key={`${evaluation.model}-${evaluation.timestamp || evaluation.created_at}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{evaluation.model || 'Unknown'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-lg font-bold ${getScoreColor(evaluation.score)}`}>
                        {evaluation.score ? formatPercentage(evaluation.score) : 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(evaluation.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-900 dark:text-gray-300">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {formatDuration(evaluation.duration)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                      {formatTimestamp(evaluation.timestamp || evaluation.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {evaluation.dataset || evaluation.dataset_name || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewContent;
